import useDictionary from 'lib/sitecore/use-dictionary';
import {
  OnLinkDataLayerPushProps,
  SearchLinkDataLayerObject,
  gtmDatalayerPush,
} from 'lib/util/gtm-datalayer';
import { classnames } from 'tailwindcss-classnames';
import { HitType } from './Hits/_types';

export const renderDate = (result: string | number | Date) => {
  const dateString = new Date(result);
  const options: {} = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return (
    <div className={classnames('mb-2')}>{dateString.toLocaleDateString(undefined, options)}</div>
  );
};
export function defaultFilter(): string {
  const todayDate = new Date();
  todayDate.setHours(24, 0, 0, 0);
  return `startDate >= ${todayDate.getTime()} OR startDate = 0 OR endDate >= ${todayDate.getTime()}`;
}

export const useSearchDictionary = () => {
  const dictionary = useDictionary();

  return {
    author: dictionary.Search_Facet_Article_Author_Label ?? '',
    articleDate: dictionary.Search_Facet_Article_Date_Label,
    articleYear: dictionary.Search_Facet_Article_Year_Label,
    classification: dictionary.Search_Facet_Employee_Classification_Label,
    department: dictionary.Search_Facet_Employee_Department_Label,
    eventCategory: dictionary.Search_Facet_Event_EventCategory_Label,
    eventDate: dictionary.Search_Facet_Event_Date_Label,
    fileType: dictionary.Search_Facet_File_FileType_Label,
    showMore: dictionary.Search_Facet_ShowMore_Label,
    selectDate: dictionary.Search_Datepicker_SelectDate_PlaceholderText,
    fromDate: dictionary.Search_Datepicker_From_Label,
    toDate: dictionary.Search_Datepicker_To_Label,
    allResult: dictionary.Search_Facet_AllResults,
    noResultsFound: dictionary.Search_No_Results_Found,
    sortRelevancy: dictionary.Search_Sort_Relevancy_Label,
    sortDate: dictionary.Search_Sort_Date_Label,
    sortFirstNameAsc: dictionary.Search_Sort_FirstNameAsc_Label,
    sortFirstNameDesc: dictionary.Search_Sort_FirstNameDesc_Label,
    sortLastNameAsc: dictionary.Search_Sort_LastNameAsc_Label,
    sortLastNameDesc: dictionary.Search_Sort_LastNameDesc_Label,
    eventType: dictionary.Search_Category_Events,
    articleType: dictionary.Search_Category_Article,
    employeeType: dictionary.Search_Category_Employee,
    otherType: dictionary.Search_Category_Other,
    fileCategoryType: dictionary.Search_Category_File,
    filter: dictionary.Search_Filter_Label,
    clearFilter: dictionary.Search_Filter_Clear_Label,
    searchFilterClearAll: dictionary.Search_Filter_ClearAll,
    sort: dictionary.Search_Sort_Label,
    resultsCount: dictionary.Search_Result_Count,
    resultsCountNoQuery: dictionary.Search_Result_Count_No_Query,
    manager: dictionary.Search_Facet_Employee_Manager_Label,
    openinSameWindow: dictionary.Open_Same_Window,
    announcementSortRelevant: dictionary.Announcement_Sort_Releavant,
    announcementSortDate: dictionary.Announcement_Sort_Date,
    announcementFilter: dictionary.Announcement_Filter,
    month: dictionary.Announcement_Facet_Month,
    year: dictionary.Announcement_Facet_Year,
    audience: dictionary.Announcement_Facet_Audience,
    all: dictionary.Nrc_Search_All,
    noresults: dictionary.Announcement_NoResults,
    sortDateDesc: dictionary.Search_Sort_Newest_To_Oldest,
    sortDateAsc: dictionary.Search_Sort_Oldest_To_Newest,
    sortNameAsc: dictionary.Search_Sort_NameAsc_Label,
    sortNameDesc: dictionary.Search_Sort_NameDesc_Label,
    format: dictionary.Search_Facet_Listing_Format_Label,
    topic: dictionary.Search_Facet_Listing_Topic_Label,
    disease: dictionary.Search_Facet_Listing_Disease_Label,
    educationnoresults: dictionary.Education_NoResults,
    sortOldest: dictionary.Search_Sort_Oldest,
    sortNewest: dictionary.Search_Sort_Newest,
    productListMedia: dictionary.ProductList__Facet_Media,
    productListResource: dictionary.ProductList__Facet_Resource,
    productListClinical: dictionary.ProductList__Facet_Clinical,
    productListOperational: dictionary.ProductList__Facet_Operational,
    productListPatient: dictionary.ProductList__Facet_Patient,
    productListLanguage: dictionary.ProductList__Facet_Language,
    shapcoSiteLinkLabel: dictionary.Shapco_Site_Link_Label,
    shapcoSiteLabel: dictionary.Shapco_Site_Label,
    shapcoSiteLink: dictionary.Shapco_Site_Link,
    name: dictionary.TcSearch_Name_Label,
    city: dictionary.TcSearch_City_Label,
    state: dictionary.TcSearch_State_Label,
    allos: dictionary.TcSearch_Allos_Label,
    diseaseTotal: dictionary.TcSearch_DiseaseTotal_Label,
    programType: dictionary.TcSearch_ProgramType_Label,
    survivorShip: dictionary.TcSearch_Survivorship_program_Label,
    diagnosis: dictionary.TcSearch_Diagnosis_Label,
    centerName: dictionary.TcSearch_Center_name_Label,
    survivorFacet: dictionary.TcSearch_Survivorship_Label,
    reset: dictionary.TcSearch_Reset_Label,
    optional: dictionary.TcSearch_OptionalText_Label,
    results: dictionary.TcSearch_Results_Label,
    allosToolTip: dictionary.TcSearch_TotalAllos_Tooltip_Label,
    diseaseTotalTip: dictionary.TcSearch_DiseaseTotal_Tooltip_Label,
  };
};

/**
 * Builds a search data layer objectTcSearch_Center_name_Label, from a search hit.
 */
export const buildSearchDataLayerFromHit = (
  hit?: HitType
): SearchLinkDataLayerObject | undefined => {
  if (!hit) {
    return undefined;
  }
  const contentType = hit?.searchableCategory
    ? hit?.searchableCategory?.slice(-1).toString()
    : hit?.type;
  return {
    event: 'e_result_click',
    contentType: contentType,
    contentName: hit.pageTitle,
    linkUrl: hit.url,
    topic: hit.topics?.join(', '),
    audience: hit.audience?.join(', '),
    format: hit.formats?.join(', '),
  };
};

type DefaultSearchHitDataLayerHandlerProps = OnLinkDataLayerPushProps & {
  hit?: HitType;
};

/**
 * Standard search hit data layer processing behavior
 */
export const defaultSearchHitDataLayerHandler = ({
  hit,
  preventDefault,
}: DefaultSearchHitDataLayerHandlerProps): void => {
  const hitDataLayerInfo = buildSearchDataLayerFromHit(hit);
  if (hitDataLayerInfo) {
    gtmDatalayerPush(hitDataLayerInfo);
    // Since we handled the push, return false so caller knows to suppress default event
    preventDefault();
  }
};
